import {
  Badge,
  InlineStack,
  Paragraph,
  Tooltip,
  TooltipContent,
  FormattedDateTime,
} from '@customerio/pluma-components/react';
import { CampaignStateLabels } from '@/utils/campaign-labels';
import type { Campaign } from '@/services/db/campaign/entity';
import { hasFutureSchedule } from '../campaign-schedule';

type BadgeColor = 'gray' | 'yellow' | 'red' | 'green';

/**
 * Badge colour per campaign state. Exhaustive over `Campaign['state']` rather than falling back to
 * a default, so adding a state to the API contract is a compile error instead of a silent gray
 * chip. The transient backend states (`evaluating`, `paused`, `deleting`) take gray.
 */
export const STATE_BADGE_COLORS = {
  running: 'green',
  draft: 'gray',
  stopped: 'red',
  archived: 'gray',
  sunsetting: 'red',
  stopping: 'red',
  archiving: 'gray',
  evaluating: 'gray',
  paused: 'gray',
  deleting: 'gray',
} as const satisfies Record<Campaign['state'], BadgeColor>;

type CampaignStateBadgeProps = {
  campaign: Campaign;
};

/**
 * The campaign's state chip with its explanatory tooltip, and beside it a
 * Scheduled chip while the campaign is scheduled to start or stop later.
 */
export function CampaignStateBadge({ campaign }: CampaignStateBadgeProps) {
  const { state } = campaign;

  const label = CampaignStateLabels[state] ?? state;
  const color = STATE_BADGE_COLORS[state];
  const tooltipContent = getStateTooltipContent(state);
  const isScheduled = hasFutureSchedule(campaign, Math.floor(Date.now() / 1000));

  return (
    <InlineStack gap="100" alignBlock="center">
      <Tooltip placement="bottom">
        <Badge color={color} showStatusIndicator>
          {label}
        </Badge>
        {tooltipContent == null ? null : <TooltipContent>{tooltipContent}</TooltipContent>}
      </Tooltip>
      {isScheduled ? <ScheduledBadge campaign={campaign} /> : null}
    </InlineStack>
  );
}

/** The Scheduled chip, whose tooltip says when the campaign starts and stops. */
function ScheduledBadge({ campaign }: CampaignStateBadgeProps) {
  const { scheduled_start: start, scheduled_stop: stop } = campaign;
  return (
    <Tooltip placement="bottom">
      <Badge color="yellow" showStatusIndicator>
        Scheduled
      </Badge>
      <TooltipContent>
        {start == null || start === 0 ? null : (
          <Paragraph color="inherit">
            Starts:{' '}
            <FormattedDateTime date={start} format="medium_with_time" dateTimeFormatOptions={{ second: undefined }} />
          </Paragraph>
        )}
        {stop == null || stop === 0 ? null : (
          <Paragraph color="inherit">
            Stops:{' '}
            <FormattedDateTime date={stop} format="medium_with_time" dateTimeFormatOptions={{ second: undefined }} />
          </Paragraph>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Per-state tooltip copy. Returns `null` for the states that have no explanatory copy, so the
 * badge renders without a tooltip rather than with an empty one.
 */
function getStateTooltipContent(state: Campaign['state']): string | null {
  switch (state) {
    case 'archiving':
      return 'This Automation is in the process of being archived.';
    case 'archived':
      return "Automation is archived and doesn't send any messages.";
    case 'draft':
      return 'Not yet matching profiles or sending messages';
    case 'running':
      return 'Automation triggers and message workflow are live.';
    case 'sunsetting':
      return 'This Automation will stop after all in progress people have finished their journey.';
    case 'stopping':
      return 'This Automation is in the process of being stopped.';
    case 'stopped':
      return "Automation is stopped and doesn't send any messages.";
    default:
      return null;
  }
}
